import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_ImageSplitter = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/image-splitter',
			name: 'Image Splitter — Split Photo Into Equal Grid Tiles Online | Lift',
			description:
				'Set rows and columns, preview your grid live, and split any photo into equal tiles in seconds. Download all parts as a ZIP. Try Lift Image Splitter — no watermarks, no installs.',
			url: 'https://liftapp.ai/image-splitter',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/image-splitter#product' },
				{ '@id': 'https://liftapp.ai/image-splitter#howto' },
				{ '@id': 'https://liftapp.ai/image-splitter#benefits' },
				{ '@id': 'https://liftapp.ai/image-splitter#faq' },
				{ '@id': 'https://liftapp.ai/image-splitter#why-lift' },
				{ '@id': 'https://liftapp.ai/image-splitter#explore-tools' },
				{ '@id': 'https://liftapp.ai/image-splitter#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/image-splitter#product',
			name: 'Image Splitter — Split Photo Into Equal Grid Tiles',
			description:
				"Split any photo into equal grid tiles with Lift's Image Splitter. Set rows and columns, preview the grid live, and download all parts as a ZIP archive in seconds.",
			url: 'https://liftapp.ai/image-splitter',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/image-splitter#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photo',
					text: "Choose a photo from your device or take one using Lift's app to start the splitting process.",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Set your grid',
					text: 'Use the Rows and Columns controls to define your grid. The dotted overlay updates in real time on your photo — no guessing required.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Split the image',
					text: 'Click Split. Your photo is divided into equal tiles and displayed as individual images in a clean grid layout.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download all parts',
					text: 'Click Download ZIP to save every tile in one archive. Need a different grid? Adjust the settings and split again at any time.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-splitter#benefits',
			name: 'Who can benefit from Image Splitter?',
			description:
				"Lift's Image Splitter is useful for Instagram creators, designers, and everyday users who want to divide photos into equal grid tiles for social media, print layouts, or online sharing.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Instagram users can split a single photo into a seamless 3×3 or 3×4 grid and post each tile as a separate image.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Content creators can prepare multi-panel carousel posts by slicing one image into individual parts.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Designers can generate precisely equal tiles for multi-panel layouts or print-ready assets.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Mobile users can set rows and columns with a tap, preview the result instantly, and download all tiles in one ZIP.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Everyday users can divide any photo into equal parts online without installing software.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Lift Image Splitter — photo split into grid tiles',
				description:
					'Example of how Lift Image Splitter divides a photo into equal grid tiles ready for download.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/image-splitter#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Image Splitter?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Image Splitter is a photo splitting tool built into the Lift editor. It divides any photo into equal grid tiles based on the number of rows and columns you set. The result is a set of perfectly matched parts you can download as a ZIP archive.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Image Splitter work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Simply upload your photo from your device, adjust the grid using \u2013 and + buttons, click Split to generate the tiles, and Download ZIP to save them all at once.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I choose how many parts to split my image into?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift Image Splitter lets you set both Rows and Columns independently using \u2013 and + controls, so you can split your photo into any grid size you need.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I preview the grid before splitting?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. As soon as you open the Split modal, a grid is drawn over your photo. It updates in real time with every Rows or Columns change — before you click Split.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-splitter#why-lift',
			name: 'Why use Lift Image Splitter',
			description:
				"Lift's Image Splitter combines ease of use, live grid preview, and instant ZIP download — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'Achieve studio-quality images without the need for complex tools',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift Image Splitter',
				description:
					'Three-step visual showing Lift photo splitter for Instagram — source photo, grid overlay, and Instagram profile mosaic result.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-splitter#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/image-splitter#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Image Splitter',
					item: 'https://liftapp.ai/image-splitter',
				},
			],
		},
	],
};
